// =============================================================================
// AppErrorBoundary — ROOT error boundary
// =============================================================================
// Lapie crashe w React tree ktore inaczej daja czarny ekran (np. bug w
// komponencie mounted po auth recovery). Pokazuje fallback z opcja reload
// + "zglos blad" info. Error logowany do console + optional Sentry-like hook.
//
// Uzywany w App.tsx jako outer wrapper okolo <BrowserRouter>.

import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class AppErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null, errorInfo: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("[AppErrorBoundary] caught:", error, errorInfo);
    this.setState({ errorInfo });
  }

  handleReload = () => {
    // Clear caches + hard reload
    try {
      if ("caches" in window) {
        caches.keys().then((keys) => keys.forEach((k) => caches.delete(k)));
      }
    } catch { /* ignore */ }
    window.location.reload();
  };

  handleClearStorage = () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch { /* ignore */ }
    this.handleReload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const msg = this.state.error?.message ?? "Nieznany blad";
    const stack = this.state.error?.stack ?? "";

    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
        <div className="max-w-xl w-full space-y-4">
          <div className="flex items-center gap-2 text-red-500">
            <AlertTriangle className="h-6 w-6" />
            <h1 className="text-xl font-semibold">Aplikacja napotkala blad</h1>
          </div>

          <div className="border border-red-500/30 bg-red-500/5 rounded-md p-3 space-y-2">
            <p className="text-sm font-medium text-red-700 dark:text-red-400">Error:</p>
            <pre className="text-xs font-mono overflow-x-auto text-foreground whitespace-pre-wrap">{msg}</pre>
          </div>

          {stack && (
            <details className="text-xs" open>
              <summary className="cursor-pointer text-muted-foreground hover:text-foreground">Stack trace</summary>
              <pre className="mt-2 p-2 bg-muted/30 rounded text-[10px] font-mono overflow-x-auto max-h-[30vh] overflow-y-auto whitespace-pre-wrap">{stack}</pre>
            </details>
          )}

          {this.state.errorInfo?.componentStack && (
            <details className="text-xs" open>
              <summary className="cursor-pointer text-amber-600 dark:text-amber-400 font-medium">⚠ React Component stack (KTÓRY komponent crashował)</summary>
              <pre className="mt-2 p-2 bg-amber-500/5 border border-amber-500/20 rounded text-[10px] font-mono overflow-x-auto max-h-[30vh] overflow-y-auto whitespace-pre-wrap text-amber-900 dark:text-amber-200">{this.state.errorInfo.componentStack}</pre>
            </details>
          )}

          <div className="flex flex-col gap-2 pt-2">
            <button
              onClick={this.handleReload}
              className="flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium"
            >
              <RefreshCw className="h-4 w-4" />
              Przeladuj (czysty cache)
            </button>
            <button
              onClick={this.handleClearStorage}
              className="flex items-center justify-center gap-2 px-4 py-2 rounded-md border border-border bg-card hover:bg-muted text-sm"
            >
              Wyloguj + wyczysc localStorage + przeladuj
            </button>
          </div>

          <p className="text-[11px] text-muted-foreground pt-2">
            Jesli problem powraca po reload — prawdopodobnie przegladarka ma zbuforowany
            stary bundle. Ctrl+Shift+R zmusi swieze pobieranie.
          </p>
        </div>
      </div>
    );
  }
}
